/**
 * PageLock — under-construction overlay wrapper for in-progress feature pages.
 *
 * Wraps a route's content. Reads the page-lock manifest to decide which of
 * three states to render:
 *
 *   1. Locked — the page is in the manifest and the user has not bypassed
 *      this specific PageLock instance. The real page content renders
 *      underneath, but is blurred and made inert (no pointer events, no
 *      text selection, no focusable elements). A glassmorphic overlay sits
 *      on top with a frosted-glass card listing the page's work stages.
 *      The user can see the *shape* of the upcoming page through the blur,
 *      which is the point of the design.
 *
 *   2. Unlocked, manifest entry present — the user has clicked "Show me
 *      anyway" for this specific page view. Renders the children plus a
 *      small "In Progress" pill pinned to the bottom-right of the
 *      viewport. Clicking the pill re-locks the page immediately.
 *
 *      The pill uses `position: fixed` at the bottom-right rather than
 *      `position: absolute` at the top-right because top-right is a hot
 *      zone for page-level toolbars (refresh buttons, status badges,
 *      filters etc.) and we kept getting collisions. The bottom-right of
 *      the viewport is consistently empty across the app, so anchoring
 *      there guarantees the pill never sits on top of existing page
 *      controls regardless of which page is unlocked.
 *
 *   3. No manifest entry — the page is fully released. Renders children
 *      unchanged, no indicator.
 *
 * Bypass scope: the "Show me anyway" state lives in React local state on
 * this component instance. That makes every behaviour the user expects fall
 * out for free:
 *   - Per-page: each PageLock has its own bypass state, so unlocking one
 *     locked page does not unlock any other locked page.
 *   - Per-view: the state is destroyed when the component unmounts (the
 *     normal effect of route navigation), so navigating away and back
 *     re-locks the page.
 *   - Non-persistent: nothing is written to localStorage, so the lock is
 *     also restored across app restarts.
 *
 * Dev overrides via `.dev/page-locks.json` continue to work for pairing /
 * agent sessions — see `src/lib/page-locks/resolver.ts`.
 */

import { useState, type CSSProperties, type ReactNode } from 'react'
import { Lock, AlertTriangle, Check, Circle } from 'lucide-react'

import {
  PAGE_LOCKS,
  devOverrideFor,
  hasLockEntry,
  type PageLock as PageLockEntry,
  type StageStatus,
  type WorkStage,
} from '@/lib/page-locks'

// ── Public component ─────────────────────────────────────────────────────────

export interface PageLockProps {
  /**
   * Manifest key for this page. Convention: matches the feature folder name
   * under `src/features/`. If the key is not in the manifest, this wrapper
   * is a no-op.
   */
  pageId: string
  /** The real page content. Only rendered when not locked. */
  children: ReactNode
}

export function PageLock({ pageId, children }: PageLockProps) {
  // Per-mount bypass state. Local to this component instance — when the
  // route unmounts (the user navigates away), React destroys this state.
  // On the next mount (navigation back) it starts at `false`, so the lock
  // is restored automatically. Hooks must run unconditionally, so we call
  // useState *before* any of the manifest / dev-override early returns
  // below.
  const [bypassed, setBypassed] = useState(false)

  const entry = PAGE_LOCKS[pageId]

  // Page is not in the manifest — render the children unchanged. This is
  // the steady-state path for shipped pages.
  if (!hasLockEntry(pageId) || !entry) {
    return <>{children}</>
  }

  // Resolution order: dev override file → per-mount bypass → build default
  // (locked). There is no global / persisted user override — each PageLock
  // instance manages its own bypass independently.
  const dev = devOverrideFor(pageId)
  const locked =
    dev === 'unlocked'
      ? false
      : dev === 'locked'
        ? true
        : !bypassed

  if (locked) {
    // The page renders behind, blurred and inert, so the user sees the shape
    // of the upcoming feature without being able to interact with it.
    return (
      <div style={containerStyles.locked} data-testid="page-lock-container">
        <div
          style={containerStyles.lockedBehind}
          aria-hidden="true"
          // inert is the standards-track way to disable a subtree; combined
          // with pointer-events: none and tabindex stripping it ensures the
          // page underneath cannot be focused or interacted with.
          {...({ inert: '' } as { inert: string })}
        >
          {children}
        </div>
        <LockOverlay entry={entry} onBypass={() => setBypassed(true)} />
      </div>
    )
  }

  // Unlocked but in the manifest — page is in progress, user has opted in
  // for this mount only.
  return (
    <div style={containerStyles.unlocked}>
      {children}
      <WarningIndicator entry={entry} onReLock={() => setBypassed(false)} />
    </div>
  )
}

// ── Lock overlay ─────────────────────────────────────────────────────────────

interface LockOverlayProps {
  entry: PageLockEntry
  /** Invoked when the user clicks "Show me anyway". */
  onBypass: () => void
}

function LockOverlay({ entry, onBypass }: LockOverlayProps) {
  return (
    <div style={overlayStyles.container} data-testid="page-lock-overlay">
      <div style={overlayStyles.card} role="dialog" aria-modal="true" aria-labelledby="page-lock-title">
        <span style={overlayStyles.statusBadge} aria-label="Page status: in progress">
          In Progress
        </span>

        <div style={overlayStyles.iconWrap} aria-hidden="true">
          <Lock size={22} strokeWidth={1.5} />
        </div>

        <div style={overlayStyles.heading}>
          <h2 id="page-lock-title" style={overlayStyles.title}>
            {entry.title}
          </h2>
          {entry.subtitle && <p style={overlayStyles.subtitle}>{entry.subtitle}</p>}
        </div>

        <p style={overlayStyles.description}>{entry.description}</p>

        <StagesList stages={entry.stages} />

        {entry.estimatedReady && (
          <p style={overlayStyles.eta}>
            <span style={overlayStyles.etaLabel}>Target</span>
            <span style={overlayStyles.etaValue}>{entry.estimatedReady}</span>
          </p>
        )}

        <div style={overlayStyles.actions}>
          <button
            type="button"
            style={overlayStyles.bypassButton}
            onClick={onBypass}
            data-testid="page-lock-bypass-button"
          >
            Show me anyway
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Warning indicator (shown when bypass is on) ──────────────────────────────

interface WarningIndicatorProps {
  entry: PageLockEntry
  /** Invoked when the user clicks the pill to re-lock the page. */
  onReLock: () => void
}

function WarningIndicator({ entry, onReLock }: WarningIndicatorProps) {
  // Click the pill to re-lock immediately. Resets the per-mount bypass
  // state in the parent PageLock, which sends the overlay back up.
  return (
    <button
      type="button"
      style={indicatorStyles.button}
      onClick={onReLock}
      title={`Re-enable lock for ${entry.title}`}
      aria-label={`Re-enable lock for ${entry.title}`}
      data-testid="page-lock-warning-indicator"
    >
      <AlertTriangle size={14} strokeWidth={2} />
      <span style={indicatorStyles.label}>In Progress</span>
    </button>
  )
}

// ── Stages list ──────────────────────────────────────────────────────────────

interface StagesListProps {
  stages: WorkStage[]
  compact?: boolean
}

function StagesList({ stages, compact = false }: StagesListProps) {
  const rowGap = compact ? 6 : 10
  const itemPad = compact ? 4 : 6
  return (
    <ul style={{ ...stagesStyles.list, gap: rowGap }} aria-label="Work stages">
      {stages.map((stage, i) => (
        <li key={i} style={{ ...stagesStyles.item, padding: `${itemPad}px 0` }}>
          <StageMarker status={stage.status} />
          <div style={stagesStyles.text}>
            <span
              style={{
                ...stagesStyles.label,
                color:
                  stage.status === 'pending'
                    ? 'var(--text-tertiary)'
                    : 'var(--text-primary)',
                textDecoration: stage.status === 'complete' ? 'line-through' : 'none',
                opacity: stage.status === 'complete' ? 0.7 : 1,
              }}
            >
              {stage.label}
            </span>
            {stage.note && <span style={stagesStyles.note}>{stage.note}</span>}
          </div>
        </li>
      ))}
    </ul>
  )
}

function StageMarker({ status }: { status: StageStatus }) {
  if (status === 'complete') {
    return (
      <span style={{ ...markerStyles.base, ...markerStyles.complete }} aria-label="Complete">
        <Check size={11} strokeWidth={2.5} />
      </span>
    )
  }
  if (status === 'in-progress') {
    return (
      <span style={{ ...markerStyles.base, ...markerStyles.inProgress }} aria-label="In progress">
        <span style={markerStyles.inProgressDot} />
      </span>
    )
  }
  return (
    <span style={{ ...markerStyles.base, ...markerStyles.pending }} aria-label="Pending">
      <Circle size={8} strokeWidth={1.5} />
    </span>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const containerStyles: Record<string, CSSProperties> = {
  unlocked: {
    position: 'relative',
    width: '100%',
    // No height constraint when unlocked — children flow naturally and the
    // route's <main> handles scrolling via overflow:auto. Setting any
    // explicit height here would prevent the page from growing past the
    // viewport.
  },
  locked: {
    position: 'relative',
    // Bleed out over `<main>`'s 16/20 px padding so the overlay covers the
    // full route content area edge-to-edge. Without this the frosted-glass
    // backdrop has a visible gutter where the unblurred page shows through.
    //
    // NOTE: these values must match the `padding` on AppShell.tsx's `main`
    // styles object. If that padding changes, update these accordingly.
    width: 'calc(100% + 40px)',
    height: 'calc(100% + 32px)',
    margin: '-16px -20px',
    // Pinned to the (extended) route content area's exact height. The
    // overlay and the blurred page beneath are both `position: absolute;
    // inset: 0` of this box, so they sit in the viewport regardless of
    // how tall the underlying page wants to be.
    //
    // Clip anything that escapes — defence in depth against descendants
    // that try to grow this container.
    overflow: 'hidden',
  },
  lockedBehind: {
    // Absolutely positioned so the children's natural height does NOT
    // grow `.locked`. Without this, a tall page (e.g. a long list view)
    // would push the overlay far below the viewport and the card would
    // disappear off-screen.
    //
    // The inset values re-apply main's padding so the underlying page
    // renders in its natural position; only the *overlay* (sibling) covers
    // the bled-out area. Mirror the padding values from AppShell.tsx's
    // `main` element.
    position: 'absolute',
    top: 16,
    right: 20,
    bottom: 16,
    left: 20,
    overflow: 'hidden',
    // Light blur on the underlying page — just enough to make it clearly
    // "behind glass" without obscuring its structure. Combined with the
    // overlay's backdrop-filter the user can still see what kind of page
    // is coming, which is the whole point of glassmorphic locking.
    filter: 'blur(1px)',
    pointerEvents: 'none',
    userSelect: 'none',
  },
}

const overlayStyles: Record<string, CSSProperties> = {
  container: {
    position: 'absolute',
    inset: 0,
    // Light dark tint plus a soft backdrop blur — enough to mark the
    // overlay as a layer above the page, but not so much that the page
    // structure becomes invisible. The radial highlight keeps the centre
    // of the overlay slightly brighter so the card feels lit.
    background:
      'radial-gradient(ellipse at center, rgba(20,20,30,0.22) 0%, rgba(10,10,15,0.40) 100%)',
    backdropFilter: 'blur(6px) saturate(125%)',
    WebkitBackdropFilter: 'blur(6px) saturate(125%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    zIndex: 50,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    // Slight transparency on the card itself plus its own backdrop-filter
    // gives it the layered glassmorphic look against the already-blurred
    // overlay. Border uses a soft white at low opacity, the canonical
    // glassmorphic edge.
    background: 'rgba(28, 28, 36, 0.72)',
    backdropFilter: 'blur(16px) saturate(150%)',
    WebkitBackdropFilter: 'blur(16px) saturate(150%)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: 'var(--radius-lg, 14px)',
    padding: '28px',
    boxShadow:
      '0 24px 60px rgba(0,0,0,0.55), 0 4px 14px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.04)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  // "In Progress" pill rendered at the very top of the card. Pinned at the
  // top via the card's column flex order — it's the first child so it sits
  // above everything else, including the lock icon. Centered horizontally
  // via `alignSelf: center`. Amber palette mirrors the warning-indicator
  // pill shown when the page is unlocked.
  statusBadge: {
    alignSelf: 'center',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 12px',
    background: 'rgba(245, 158, 11, 0.14)',
    color: 'rgb(245, 158, 11)',
    border: '1px solid rgba(245, 158, 11, 0.32)',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontFamily: 'inherit',
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: 'var(--bg-tertiary, rgba(255,255,255,0.04))',
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--border-subtle)',
  },
  heading: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: '12px',
    color: 'var(--text-tertiary)',
    margin: 0,
    lineHeight: 1.4,
  },
  description: {
    fontSize: '13px',
    lineHeight: 1.55,
    color: 'var(--text-secondary)',
    margin: 0,
  },
  eta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    margin: '4px 0 0 0',
  },
  etaLabel: {
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    fontSize: '10px',
    letterSpacing: '0.05em',
    fontWeight: 600,
  },
  etaValue: {
    color: 'var(--text-secondary)',
    fontWeight: 500,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '4px',
  },
  bypassButton: {
    background: 'transparent',
    border: '1px solid var(--border-subtle)',
    color: 'var(--text-secondary)',
    padding: '8px 14px',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: 'var(--radius-md, 6px)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
}

const stagesStyles: Record<string, CSSProperties> = {
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    borderTop: '1px solid var(--border-subtle)',
    borderBottom: '1px solid var(--border-subtle)',
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  note: {
    fontSize: '11px',
    color: 'var(--text-tertiary)',
    lineHeight: 1.4,
  },
}

const markerStyles: Record<string, CSSProperties> = {
  base: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 2,
  },
  complete: {
    background: 'var(--color-success, rgb(34, 197, 94))',
    color: 'white',
  },
  inProgress: {
    background: 'transparent',
    border: '1.5px solid var(--color-warning, rgb(245, 158, 11))',
  },
  inProgressDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--color-warning, rgb(245, 158, 11))',
  },
  pending: {
    color: 'var(--text-tertiary)',
  },
}

const indicatorStyles: Record<string, CSSProperties> = {
  // Anchored to the bottom-right of the viewport rather than the top-right
  // of the route container. Top-right is where pages put their own
  // toolbars (Inbox's Refresh button + status pill, Sessions' filter bar,
  // etc.), so an absolute-top-right indicator collided with real controls.
  // The bottom-right of the viewport is a consistently empty area across
  // the app — there are no FABs or footer widgets — so a fixed pill there
  // is guaranteed not to overlap existing page elements.
  button: {
    position: 'fixed',
    bottom: 16,
    right: 16,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 10px',
    background: 'rgba(245, 158, 11, 0.12)',
    color: 'var(--color-warning, rgb(245, 158, 11))',
    border: '1px solid rgba(245, 158, 11, 0.3)',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 500,
    fontFamily: 'inherit',
    cursor: 'pointer',
    // Sit above route content but below modals/overlays. The lock overlay
    // uses zIndex: 50, so 40 keeps the indicator below it if both ever
    // render concurrently.
    zIndex: 40,
  },
  label: {
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: '0.01em',
  },
}
